import type { WorkflowVersion, WorkflowVersionNodeSnapshot } from '@cortex/types';

export type DiffLineKind = 'context' | 'add' | 'del';

export type DiffSpan = { kind: DiffLineKind; text: string };

export type DiffLine = {
  kind: DiffLineKind;
  text: string;
  spans?: DiffSpan[];
};

function canonicalizeJson(value: unknown): unknown {
  if (value === null) return null;
  if (Array.isArray(value)) return value.map((item) => canonicalizeJson(item));
  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      result[key] = canonicalizeJson(source[key]);
    }
    return result;
  }
  return value;
}

export function canonicalJson(value: unknown): string {
  const json = JSON.stringify(canonicalizeJson(value), null, 2);
  return json ?? String(value);
}

/**
 * Workflow-level fields a version cut can change without touching a single
 * node or edge — exactly what the builder's Schema panel edits. The diff
 * endpoint reports only nodes and edges, so a Schema-only save produces an
 * otherwise-empty diff; these are read off the two version rows instead.
 *
 * An allowlist rather than "every key of `WorkflowVersion` minus the boring
 * ones", because the exclusion list is twice as long and fail-open: a new
 * identity/provenance column would silently start showing up as a change.
 * The trade-off is that a NEW workflow-level setting must be added here to
 * appear in the diff — `Pick<>` below catches renames, not additions.
 */
export const versionSettingsFields = [
  'inputSchema',
  'outputSchema',
  'outputMapping',
  'dedupEnabled',
  'signalKeyTemplate',
] as const;

export type VersionSettingsField = (typeof versionSettingsFields)[number];

/**
 * Every `WorkflowVersion` column that is deliberately NOT part of the
 * workflow-level contract: row identity, provenance, and the `graph` blob. These
 * differ between any two versions by definition (or, for `graph`, are already
 * covered by the node/edge diff off the authoritative tables), so surfacing them
 * would be noise.
 */
type NonSettingsVersionField =
  | 'id'
  | 'workflowId'
  | 'versionNumber'
  | 'graph'
  | 'status'
  | 'attribution'
  | 'createdAt'
  | 'createdByUserId'
  | 'source'
  | 'promotedBy'
  | 'promotedAt'
  | 'experimentId';

/** Fails to compile unless `T` is `never`. */
type MustBeNever<T extends never> = T;

/**
 * Compile-time exhaustiveness guard, and the reason the allowlist above is safe
 * to maintain by hand.
 *
 * `versionSettingsFields` is fail-closed: a new workflow-level column that nobody
 * adds to it simply never appears in the diff — the same silent-omission bug this
 * whole module exists to fix, one field narrower. A comment can't enforce that.
 * This can: every key of `WorkflowVersion` must be classified as either a
 * settings field or an explicit non-setting, so adding a column to the Prisma
 * schema and the Zod type breaks the build here with
 * `TS2344: Type '"newColumn"' does not satisfy the constraint 'never'` until
 * someone decides which bucket it belongs in.
 *
 * Exported only because `noUnusedLocals` is on; it has no runtime footprint.
 */
export type VersionFieldsAreExhaustive = MustBeNever<
  Exclude<keyof WorkflowVersion, VersionSettingsField | NonSettingsVersionField>
>;

/**
 * The workflow-level contract of one version as a single canonical JSON blob —
 * the settings analogue of {@link nodeSnapshotText}. Rendering all five fields
 * as one document (rather than one block per field) means unchanged fields show
 * up as context lines, so a reviewer sees the whole contract with the changes
 * situated in it, and it matches how a changed node already renders.
 *
 * Key order in the output is alphabetical, not the order of
 * `versionSettingsFields` — `canonicalJson` sorts, which is the whole point
 * (a stable document across versions). Iterating the allowlist here is about
 * *which* keys are included, not the order they land in.
 *
 * The `?? null` is load-bearing, not defensive noise: `canonicalJson(undefined)`
 * returns the literal string `"undefined"` (`JSON.stringify(undefined)` is
 * `undefined`, so it falls through to `String(value)`). An absent key would
 * therefore render as `"undefined"` on one side and `null` on the other. It must
 * be `??` and not `||`, or `dedupEnabled: false` would render as `null`. Note
 * `null` here means "absent or unset" — for `dedupEnabled`, whose own type is
 * `z.boolean()` with a Prisma `@default(false)`, that state is unreachable
 * through the API, which always serialises the column.
 */
export function versionSettingsText(version: Pick<WorkflowVersion, VersionSettingsField>): string {
  const payload: Record<string, unknown> = {};
  for (const field of versionSettingsFields) payload[field] = version[field] ?? null;
  return canonicalJson(payload);
}

/**
 * The workflow-level delta between two versions. Mirrors the shape the diff
 * endpoint uses for a changed node (`changedFields` + before/after snapshots):
 * `changedFields` drives the summary badge and tooltip, while `before`/`after`
 * are the full canonical documents fed to {@link diffLines}.
 */
export type VersionSettingsDiff = {
  changedFields: VersionSettingsField[];
  before: string;
  after: string;
};

/**
 * Compare the workflow-level contract of two versions, or `null` when they are
 * identical. Values are compared as canonical JSON so key reordering isn't
 * reported as a change — the Schema panel rewrites whole objects on save, so a
 * raw JSON.stringify comparison would produce phantom diffs with no visible
 * changed lines.
 */
export function diffVersionSettings(
  before: Pick<WorkflowVersion, VersionSettingsField>,
  after: Pick<WorkflowVersion, VersionSettingsField>,
): VersionSettingsDiff | null {
  // `?? null` for the same reason as `versionSettingsText` — see its docstring.
  // Without it an absent field compares `"undefined"` against `"null"` and is
  // reported as changed, while the rendered document shows `null` on both sides:
  // exactly the phantom diff with no visible changed lines this function exists
  // to prevent.
  const changedFields = versionSettingsFields.filter(
    (field) => canonicalJson(before[field] ?? null) !== canonicalJson(after[field] ?? null),
  );
  if (changedFields.length === 0) return null;
  return {
    changedFields,
    before: versionSettingsText(before),
    after: versionSettingsText(after),
  };
}

/**
 * Resolves an opaque id reference in a node config to a human-readable label.
 * Called per `(fieldName, stringValue)`; return a label to substitute, or
 * `null` to leave the raw value untouched. Lets the diff show
 * `"modelConfigId": "Claude Opus 4.1 (anthropic · …)"` instead of a cuid.
 */
export type RefResolver = (field: string, value: string) => string | null;

/**
 * Walk an object/array, replacing any string leaf whose key resolves via
 * `resolveRef` with the returned label. Only string values are candidates;
 * structure is otherwise preserved so the canonical diff stays stable.
 */
function humanizeRefs(value: unknown, resolveRef: RefResolver): unknown {
  if (Array.isArray(value)) return value.map((item) => humanizeRefs(item, resolveRef));
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(source)) {
      if (typeof val === 'string') {
        const label = resolveRef(key, val);
        result[key] = label ?? val;
      } else {
        result[key] = humanizeRefs(val, resolveRef);
      }
    }
    return result;
  }
  return value;
}

export function nodeSnapshotText(
  snap: WorkflowVersionNodeSnapshot,
  resolveRef?: RefResolver,
): string {
  const config = resolveRef ? humanizeRefs(snap.config, resolveRef) : snap.config;
  return canonicalJson({
    nodeType: snap.nodeType,
    config,
    inputMapping: snap.inputMapping,
    outputSchema: snap.outputSchema,
    outputMapping: snap.outputMapping,
  });
}

// When the LCS is non-zero but covers less than this fraction of the longer
// token sequence, scattered matches produce more noise than signal — skip diff.
const wordDiffMinSimilarity = 0.5;

function tokenize(s: string): string[] {
  return s.match(/\w+|\W/g) ?? (s.length > 0 ? [s] : []);
}

function diffWords(before: string, after: string): { del: DiffSpan[]; add: DiffSpan[] } | null {
  const left = tokenize(before);
  const right = tokenize(after);
  const rows = left.length;
  const cols = right.length;

  const dp: number[][] = Array.from({ length: rows + 1 }, () => Array<number>(cols + 1).fill(0));
  for (let i = rows - 1; i >= 0; i -= 1) {
    const dpRow = dp[i];
    const dpNext = dp[i + 1];
    if (!dpRow || !dpNext) continue;
    for (let j = cols - 1; j >= 0; j -= 1) {
      dpRow[j] =
        left[i] === right[j]
          ? (dpNext[j + 1] ?? 0) + 1
          : Math.max(dpNext[j] ?? 0, dpRow[j + 1] ?? 0);
    }
  }

  const lcsLen = dp[0]?.[0] ?? 0;
  const maxLen = Math.max(rows, cols);
  if (lcsLen > 0 && maxLen > 0 && lcsLen / maxLen < wordDiffMinSimilarity) {
    return null;
  }

  const delSpans: DiffSpan[] = [];
  const addSpans: DiffSpan[] = [];

  const push = (spans: DiffSpan[], kind: DiffLineKind, token: string) => {
    const last = spans[spans.length - 1];
    if (last && last.kind === kind) {
      last.text += token;
    } else {
      spans.push({ kind, text: token });
    }
  };

  let i = 0;
  let j = 0;
  while (i < rows && j < cols) {
    if (left[i] === right[j]) {
      push(delSpans, 'context', left[i] ?? '');
      push(addSpans, 'context', right[j] ?? '');
      i += 1;
      j += 1;
    } else {
      const down = dp[i + 1]?.[j] ?? 0;
      const across = dp[i]?.[j + 1] ?? 0;
      if (down >= across) {
        push(delSpans, 'del', left[i] ?? '');
        i += 1;
      } else {
        push(addSpans, 'add', right[j] ?? '');
        j += 1;
      }
    }
  }
  while (i < rows) {
    push(delSpans, 'del', left[i] ?? '');
    i += 1;
  }
  while (j < cols) {
    push(addSpans, 'add', right[j] ?? '');
    j += 1;
  }

  return { del: delSpans, add: addSpans };
}

// For each consecutive del/add run in the LCS output, zip-pair del[k] with
// add[k] and run a word-level diff. canonicalizeJson sorts all keys, so
// paired lines always refer to the same field. Unpaired lines (when del and
// add counts differ) are left without spans. All dels are pushed before all
// adds to preserve the ordering toSideBySideRows expects.
function annotateIntraLine(lines: DiffLine[]): DiffLine[] {
  const result: DiffLine[] = [];
  let i = 0;
  while (i < lines.length) {
    const curr = lines[i];
    if (!curr) {
      i += 1;
      continue;
    }
    if (curr.kind !== 'del') {
      result.push(curr);
      i += 1;
      continue;
    }
    let di = i;
    while (di < lines.length && lines[di]?.kind === 'del') di += 1;
    let ai = di;
    while (ai < lines.length && lines[ai]?.kind === 'add') ai += 1;

    const paired = Math.min(di - i, ai - di);
    const pairSpans: Array<ReturnType<typeof diffWords>> = [];
    for (let k = 0; k < paired; k += 1) {
      pairSpans.push(diffWords(lines[i + k]?.text ?? '', lines[di + k]?.text ?? ''));
    }

    for (let k = i; k < di; k += 1) {
      const line = lines[k];
      if (!line) continue;
      const spans = pairSpans[k - i];
      result.push(spans ? { kind: 'del', text: line.text, spans: spans.del } : line);
    }
    for (let k = di; k < ai; k += 1) {
      const line = lines[k];
      if (!line) continue;
      const spans = pairSpans[k - di];
      result.push(spans ? { kind: 'add', text: line.text, spans: spans.add } : line);
    }
    i = ai;
  }
  return result;
}

export type DiffRow = {
  left?: DiffLine;
  right?: DiffLine;
};

// Converts a flat diffLines() output into paired rows for side-by-side rendering.
// Context lines appear in both columns. For del/add runs, pairs up to min(dels, adds)
// onto the same row; remaining dels go left-only and remaining adds go right-only.
export function toSideBySideRows(lines: DiffLine[]): DiffRow[] {
  const rows: DiffRow[] = [];
  let i = 0;
  while (i < lines.length) {
    const curr = lines[i];
    if (!curr) {
      i += 1;
      continue;
    }
    if (curr.kind === 'context') {
      rows.push({ left: curr, right: curr });
      i += 1;
      continue;
    }
    let di = i;
    while (di < lines.length && lines[di]?.kind === 'del') di += 1;
    let ai = di;
    while (ai < lines.length && lines[ai]?.kind === 'add') ai += 1;

    const dels = lines.slice(i, di);
    const adds = lines.slice(di, ai);
    const paired = Math.min(dels.length, adds.length);

    for (let k = 0; k < paired; k += 1) rows.push({ left: dels[k], right: adds[k] });
    for (let k = paired; k < dels.length; k += 1) rows.push({ left: dels[k] });
    for (let k = paired; k < adds.length; k += 1) rows.push({ right: adds[k] });
    i = ai;
  }
  return rows;
}

export function diffLines(before: string, after: string): DiffLine[] {
  const left = before.split('\n');
  const right = after.split('\n');
  const rows = left.length;
  const cols = right.length;

  // dp[i][j] = length of the longest common subsequence of left[i:] and
  // right[j:]. Filled bottom-up so the forward walk below can greedily pick
  // the branch that preserves the most shared lines.
  const dp: number[][] = Array.from({ length: rows + 1 }, () => Array<number>(cols + 1).fill(0));
  for (let i = rows - 1; i >= 0; i -= 1) {
    const dpRow = dp[i];
    const dpNext = dp[i + 1];
    if (!dpRow || !dpNext) continue;
    for (let j = cols - 1; j >= 0; j -= 1) {
      const matches = left[i] === right[j];
      dpRow[j] = matches ? (dpNext[j + 1] ?? 0) + 1 : Math.max(dpNext[j] ?? 0, dpRow[j + 1] ?? 0);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < rows && j < cols) {
    const a = left[i] ?? '';
    const b = right[j] ?? '';
    if (a === b) {
      out.push({ kind: 'context', text: a });
      i += 1;
      j += 1;
      continue;
    }
    const down = dp[i + 1]?.[j] ?? 0;
    const across = dp[i]?.[j + 1] ?? 0;
    if (down >= across) {
      out.push({ kind: 'del', text: a });
      i += 1;
    } else {
      out.push({ kind: 'add', text: b });
      j += 1;
    }
  }

  while (i < rows) {
    out.push({ kind: 'del', text: left[i] ?? '' });
    i += 1;
  }
  while (j < cols) {
    out.push({ kind: 'add', text: right[j] ?? '' });
    j += 1;
  }

  return annotateIntraLine(out);
}
